import { BankCardDynamicData } from '../components/BankCard/BankCardDynamicData';
import { DebtType, NegotiationOption, NegotiationType, OutDebt, OutDebtState } from '../generated/graphql';
import { formatIRate } from './formatIRate';
import { getJcapInterests } from './getJcapInterests';
import { getNegBooleans } from './getNegBooleans';
import getVirtualDebtChildrenBalance from './getVirtualDebtChildrenBalance';
import { pluralize } from './pluralize';
import thousandSeparator from './thousandSeparator';

export const getBankCardInfo = (outDebt?: OutDebt, negOptions?: NegotiationOption[]) => {
  const info: BankCardDynamicData[] = [];
  if (!outDebt) return info;

  const {
    isBbva,
    isBeta,
    isPichincha,
    isBogota,
    isScotiabank,
    isJcap,
    isLulo,
    isTranqui,
    isApex,
    isApexOccidente,
    isApexPopular,
    isApexRappi,
    isPra,
    isClaro,
  } = getNegBooleans(outDebt);

  const balance = outDebt.balance || 0;
  const balanceBehind = outDebt.balanceBehind || 0;
  const monthlyPayment = outDebt.monthlyPayment || 0;
  const type = outDebt.type || '';
  const rate = outDebt.rate || 0;
  const minPayment = outDebt.minPayment || 0;
  const daysBehind = outDebt.daysBehind || 0;
  const potencialMark = outDebt.extra?.potencialMark;
  const prePotencialMark = outDebt.extra?.prePotencialMark;
  const paymentReference = outDebt.extra?.paymentReference;
  const writeOffMark = outDebt.extra?.writeOffMark;
  const isPA = type === DebtType.PayrollAdvance;
  const isCC = type === DebtType.CreditCard;
  const isAP = type === DebtType.AllPurpose;
  const isVirtual = type === DebtType.Virtual;
  const totalPaymentNegOpt = negOptions?.find((n) => n.type === NegotiationType.TotalPayment);
  const hasTotalPayment = !!totalPaymentNegOpt;
  const showMinPayment = !hasTotalPayment && minPayment < balance && minPayment > 0;
  const showBalanceBehind = !hasTotalPayment && balanceBehind < balance && balanceBehind > 0;
  const isPunished = outDebt.extra?.state === OutDebtState.Punished;
  const hasExpressRefinance = !!negOptions?.find((n) => {
    return n.type === NegotiationType.ExpressRefinance;
  });

  const getBalance = isJcap
    ? (od: OutDebt) => ((od.balance || 0) + getJcapInterests(od)) || 0
    : undefined;
  const setBalance = (override = 0) => {
    const usedBalance = override || balance;
    if (usedBalance) info.push({ label: 'Saldo total', value: `$ ${thousandSeparator(usedBalance)}` });
  };
  const setDays = () => daysBehind > 0 && info.push({ label: 'Mora', value: `${daysBehind} días` });

  const isCloseToBalance = (value: number) => Math.abs(balance - value) < 10000; // diff less to 10k

  if (isVirtual) {
    const balanceValue = `$ ${thousandSeparator(getVirtualDebtChildrenBalance(outDebt.extra?.children || [], getBalance))}`;
    info.push({ label: 'Saldo total', value: balanceValue });
    info.push({ label: 'Deudas a unificar', value: `${outDebt.extra!.children!.length} deuda${pluralize(outDebt.extra!.children!.length)}` });
    return info;
  }

  if (isJcap || isPra) {
    setBalance(getBalance?.(outDebt) || balance);
    setDays();
    return info;
  }

  if (isClaro) {
    if (paymentReference) info.push({ label: 'Referencia de pago', value: paymentReference });
    if (!potencialMark && !prePotencialMark && !writeOffMark) {
      info.push({ label: 'Cuota actual', value: `$ ${thousandSeparator(minPayment)}` });
      setDays();
      return info;
    }
    return info;
  }

  if (isBeta) {
    info.push({ label: 'Producto', value: `${outDebt.subType || ''}` });
    setBalance();
    setDays();
    return info;
  }

  if (isPichincha) {
    const paymentPromise = negOptions?.find((o) => o.type === NegotiationType.PaymentPromise);
    const ppValue = paymentPromise?.alternatives[0]?.value;
    setBalance();
    if (ppValue && ppValue < balance) {
      info.push({ label: 'Saldo en mora', value: `$ ${thousandSeparator(ppValue)}` });
    }
    setDays();
    return info;
  }

  if (isPichincha && hasExpressRefinance) {
    setBalance();
    if (showMinPayment) info.push({ label: 'Cuota actual', value: `$ ${thousandSeparator(minPayment)}` });
    setDays();
    return info;
  }

  if (isApex) {
    if (outDebt?.extra?.movistarInvoice) info.push({ label: 'Tienes factura Apex', value: outDebt?.extra?.movistarInvoice });
    if (outDebt?.daysBehind) info.push({ label: 'Días de mora', value: `${outDebt?.daysBehind}` });
    if (isApexOccidente || isApexPopular || isApexRappi) {
      info.push({ label: 'Saldo Total', value: `$ ${thousandSeparator(balance || outDebt?.balancePrincipal)}` });
      return info;
    }
    info.push({ label: 'Saldo Total', value: `$ ${thousandSeparator(outDebt?.balancePrincipal || balance)}` });
    return info;
  }

  if (isScotiabank) {
    const balanceBehindValue = showBalanceBehind ? balanceBehind : minPayment;
    setBalance();
    info.push({ label: 'Cuota actual', value: `$ ${thousandSeparator(monthlyPayment)}` });
    if (isAP && daysBehind > 0) info.push({ label: 'Saldo en mora', value: `$ ${thousandSeparator(balanceBehindValue)}` });
    setDays();
    return info;
  }

  if (isBogota && isTranqui) {
    const totalPaymentBalance = hasTotalPayment && totalPaymentNegOpt?.alternatives[0].balance;
    const newBalance = totalPaymentBalance || balance || 0;
    const value = `$ ${thousandSeparator(Math.max(minPayment, newBalance))}`;
    if (value) info.push({ label: 'Saldo último corte', value });
    const isInmediatePaymentDate = outDebt.extra?.product === 'SOBREGIRO';
    if (isInmediatePaymentDate) info.push({ label: 'Fecha de pago', value: 'Inmediato' });
    setDays();
    return info;
  }

  if (isLulo) {
    setBalance();
    setDays();
    return info;
  }

  if (isCC || isPA) {
    setBalance(getBalance?.(outDebt) || balance);
    if (rate > 0 && !isBbva) info.push({ label: 'Tasa', value: `${formatIRate(rate, 'm')}` });
    if (showMinPayment && !isPunished && !isCloseToBalance(minPayment) && !isJcap) info.push({ label: 'Cuota actual', value: `$ ${thousandSeparator(minPayment)}` });
    setDays();
  }
  if (isAP) {
    if (monthlyPayment > 0 && !isPunished && !isCloseToBalance(monthlyPayment) && !isJcap) info.push({ label: 'Cuota actual', value: `$ ${thousandSeparator(monthlyPayment)}` });
    setBalance(getBalance?.(outDebt) || balance);
    if (showBalanceBehind) info.push({ label: 'Saldo en mora', value: `$ ${thousandSeparator(balanceBehind)}` });
    if (!showBalanceBehind && showMinPayment) info.push({ label: 'Saldo en mora', value: `$ ${thousandSeparator(minPayment)}` });
    setDays();
  }

  return info;
};
